// TS port of the getPeriodData function from the original JavaScript code (get-period-data.js)
type PeriodType = 'quarter' | 'month' | 'half'
type PeriodOutputType = 'quarterly' | 'monthly' | 'half-yearly' | 'none'

export interface PeriodData {
  type: PeriodOutputType
  periodSettled: string
  year: number | null
}

const getYearNumber = (yearString: string): number | null => {
  const parsed = parseInt(yearString, 10)
  return isNaN(parsed) ? null : parsed
}

/**
 * Parses a period string (e.g., "quarter:2020-3") into a structured object containing
 * the period type, a formatted period identifier, and the year as a number.
 *
 * Recognized input types:
 * - "quarter" (e.g., "quarter:2020-3") → { type: "quarterly", periodSettled: "q3", year: 2020 }
 * - "month"   (e.g., "month:2021-12") → { type: "monthly", periodSettled: "m12", year: 2021 }
 * - "half"    (e.g., "half:2022-1")   → { type: "half-yearly", periodSettled: "h1", year: 2022 }
 *
 * If the type is unrecognized, the function returns:
 * - { type: "none", periodSettled: "-", year: parsedYear }
 *
 * If the input is missing or empty, the function returns `undefined`.
 *
 * @param period - A string representing the period, formatted as "<type>:<year>-<index>".
 * @returns A `PeriodData` object if parsing succeeds, or `undefined` if input is missing.
 *
 * @throws Will throw an error if the input is present but malformed and cannot be parsed.
 */
export const getPeriodData = (period: string): PeriodData | undefined => {
  if (!period) {
    return
  }
  try {
    const [type, value] = period.split(':', 2)
    const [yearStr, index] = value.split('-')

    const year = getYearNumber(yearStr)

    switch (type as PeriodType) {
      case 'quarter':
        return {
          type: 'quarterly',
          periodSettled: `q${index}`,
          year,
        }
      case 'month':
        return {
          type: 'monthly',
          periodSettled: `m${index}`,
          year,
        }
      case 'half':
        return {
          type: 'half-yearly',
          periodSettled: `h${index}`,
          year,
        }
      default:
        return {
          type: 'none',
          periodSettled: '-',
          year,
        }
    }
  } catch {
    throw new Error(`Cannot break period data: ${period}`)
  }
}
